import React, { useState } from 'react';
import { Mail, MessageCircle, Send, MapPin } from 'lucide-react';

const Contact = () => {
  const [formStatus, setFormStatus] = useState('idle'); // idle, sending, success, error

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus('sending');
    // Simulate sending
    setTimeout(() => {
      setFormStatus('success');
      e.target.reset();
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-hue-bg font-roboto text-hue-font pb-20">
      {/* Hero */}
      <div className="bg-hue-purple text-white pt-32 pb-24 px-6 rounded-b-[3rem] relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-7xl font-black mb-6">Contáctanos</h1>
          <p className="text-xl md:text-2xl font-medium opacity-90 max-w-2xl mx-auto">
            ¿Tienes dudas, sugerencias o quieres implementar Hue en tu comuna? Estamos aquí para escucharte.
          </p>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3"></div>
      </div>

      <div className="max-w-6xl mx-auto px-6 -mt-12 relative z-20">
        <div className="grid md:grid-cols-5 gap-8">

          {/* Contact Info Cards */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white p-8 rounded-[2.5rem] shadow-xl border border-slate-100">
              <h3 className="text-2xl font-bold mb-6">Canales Directos</h3>

              <div className="space-y-6">
                <a href="mailto:hola@hue.cl" className="flex items-center gap-4 group p-4 rounded-2xl hover:bg-slate-50 transition">
                  <div className="w-12 h-12 bg-hue-light-blue rounded-full flex items-center justify-center text-hue-purple group-hover:scale-110 transition">
                    <Mail size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Email General</p>
                    <p className="text-lg font-bold text-slate-900">hola@hue.cl</p>
                  </div>
                </a>

                <a href="https://wa.me/56991831769" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group p-4 rounded-2xl hover:bg-slate-50 transition">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600 group-hover:scale-110 transition">
                    <MessageCircle size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 font-medium">WhatsApp</p>
                    <p className="text-lg font-bold text-slate-900">+56 9 2906 6798</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-4 rounded-2xl">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Oficinas</p>
                    <p className="text-lg font-bold text-slate-900">Osorno, Chile</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-hue-olive p-8 rounded-[2.5rem] shadow-xl text-hue-dark-text relative overflow-hidden">
              <h3 className="text-2xl font-black mb-4 relative z-10">¿Eres un Municipio?</h3>
              <p className="font-medium mb-6 relative z-10">
                Agenda una demo personalizada y descubre cómo Hue puede transformar tu gestión.
              </p>
              <button className="bg-hue-dark-text text-white px-6 py-3 rounded-xl font-bold hover:bg-slate-800 transition shadow-lg relative z-10">
                Agendar Demo
              </button>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/20 rounded-full blur-2xl"></div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="md:col-span-3">
            <div className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-xl border border-slate-100 h-full">
              <h2 className="text-3xl font-black mb-8">Envíanos un mensaje</h2>

              {formStatus === 'success' ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-20">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-6 animate-bounce">
                    <Send size={40} />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">¡Mensaje Enviado!</h3>
                  <p className="text-slate-500">Te responderemos lo antes posible.</p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-8 text-hue-purple font-bold hover:underline"
                  >
                    Enviar otro mensaje
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="name" className="text-sm font-bold text-slate-700 ml-2">Nombre</label>
                      <input
                        type="text"
                        id="name"
                        required
                        className="w-full p-4 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-hue-purple/50 focus:bg-white transition outline-none font-medium"
                        placeholder="Tu nombre"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-sm font-bold text-slate-700 ml-2">Email</label>
                      <input
                        type="email"
                        id="email"
                        required
                        className="w-full p-4 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-hue-purple/50 focus:bg-white transition outline-none font-medium"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="subject" className="text-sm font-bold text-slate-700 ml-2">Asunto</label>
                    <select
                      id="subject"
                      className="w-full p-4 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-hue-purple/50 focus:bg-white transition outline-none font-medium text-slate-600"
                    >
                      <option>Consulta General</option>
                      <option>Soporte Técnico</option>
                      <option>Prensa / Medios</option>
                      <option>Alianzas</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="text-sm font-bold text-slate-700 ml-2">Mensaje</label>
                    <textarea
                      id="message"
                      required
                      rows="6"
                      className="w-full p-4 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-hue-purple/50 focus:bg-white transition outline-none font-medium resize-none"
                      placeholder="¿En qué podemos ayudarte?"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className="w-full bg-hue-purple text-white py-4 rounded-2xl font-bold text-lg hover:bg-purple-800 transition shadow-lg shadow-hue-purple/30 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'sending' ? (
                      <>Enviando...</>
                    ) : (
                      <>Enviar Mensaje <Send size={20} /></>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Contact;
